import { IndicatorSeries, type IndicatorTemplate, type IndicatorDrawParams } from 'klinecharts';

export const HLC_UP_COLOR   = '#1677ff'; // blue
export const HLC_DOWN_COLOR = '#f97898'; // pink
export const HLC_NO_CHANGE_COLOR = '#9598a1';

type HLCData = Record<string, never>;

const hlcBars: IndicatorTemplate<HLCData> = {
  name: 'HLC',
  shortName: 'HLC',
  series: IndicatorSeries.Normal,
  shouldOhlc: true,
  figures: [],

  calc: (dataList) => dataList.map(() => ({} as HLCData)),

  createTooltipDataSource: () => ({
    name: '',
    calcParamsText: '',
    icons: [],
    values: [],
  }),

  draw: (params: IndicatorDrawParams<HLCData>): boolean => {
    const { ctx, kLineDataList, visibleRange, barSpace, defaultStyles, xAxis, yAxis } = params;

    const { gapBar, halfGapBar } = barSpace;
    const { upColor, downColor } = defaultStyles.ohlc;

    // Thin bar: ~1/4 of the full slot width, centred on x
    const thinW  = Math.max(Math.round(gapBar * 0.25), 1);
    // Close tick height scales with zoom; minimum 1.5px
    const closeTickH = Math.max(1.5, Math.ceil(gapBar * 0.12));

    ctx.save();

    for (let i = visibleRange.from; i <= visibleRange.to; i++) {
      const bar = kLineDataList[i];
      if (!bar) continue;

      const { high, low, close } = bar;
      const prevClose = kLineDataList[i - 1]?.close ?? bar.open;

      const color = close >= prevClose ? upColor : downColor;

      const x      = xAxis.convertToPixel(i);
      const yHigh  = yAxis.convertToPixel(high);
      const yLow   = yAxis.convertToPixel(low);
      const yClose = yAxis.convertToPixel(close);

      const barH = Math.max(yLow - yHigh, 1);

      ctx.fillStyle = color;

      // Thin vertical bar (1/4 slot width) from low → high, centred on x
      ctx.fillRect(x - thinW / 2, yHigh, thinW, barH);

      // Close tick — full slot width (same as a regular candle body) at close level
      ctx.fillRect(
        x - halfGapBar,
        yClose - closeTickH / 2,
        Math.max(gapBar, 1),
        closeTickH,
      );
    }

    ctx.restore();
    return true;
  },
};

export default hlcBars;
